import { memo, useLayoutEffect, useRef, useState } from 'react';

import { Box, rem } from '@bonusly/design-system';

import { useBizyFlightAnchor } from '../bizy';
import { BizySidePanel } from './bizy-side-panel';
import { PANEL_OPEN_CLOSE_TRANSITION_MS } from './constants';

export interface BizySidePanelDockProps {
	isOpen: boolean;
	isFullscreen: boolean;
	isResizing: boolean;
	panelWidth: number;
	// Nav sidebar's current width in px, for sizing the fullscreen overlay.
	sidebarPx: number;
	// Sidebar's own width-transition values, shared so the overlay's left/width can't drift from them.
	sidebarTransitionMs: number;
	sidebarTransitionEasing: string;
	top: string;
	height: string;
}

/**
 * The Bizy panel's own column in AppLayout's flex row — a real flex sibling
 * of the sidebar and main content, so widths stay in sync automatically.
 *
 * Fullscreen never grows the flex reservation past the docked `panelWidth`,
 * so main content never resizes on fullscreen toggle. It overlays instead via
 * `position: fixed`, only while open AND fullscreen.
 */
export const BizySidePanelDock = memo(function BizySidePanelDock({
	isOpen,
	isFullscreen,
	isResizing,
	panelWidth,
	sidebarPx,
	sidebarTransitionMs,
	sidebarTransitionEasing,
	top,
	height,
}: BizySidePanelDockProps) {
	const bizyPanelFallbackRef = useBizyFlightAnchor('navFallback');

	const dockedWidth = rem(panelWidth);
	// Reservation is ALWAYS the docked width (or 0 closed) — never the
	// fullscreen width. Main only ever shrinks by this much, fullscreen or not.
	const flexBasis = isOpen ? dockedWidth : '0px';
	const isOverlaying = isOpen && isFullscreen;

	// Snap left/width instantly on entering/exiting the overlay itself, but
	// animate them in sync with the sidebar once settled in fullscreen.
	const prevIsOverlayingRef = useRef(isOverlaying);
	const [isEnteringOrExitingOverlay, setIsEnteringOrExitingOverlay] = useState(false);
	useLayoutEffect(() => {
		if (prevIsOverlayingRef.current === isOverlaying) return;
		prevIsOverlayingRef.current = isOverlaying;
		setIsEnteringOrExitingOverlay(true);
		const timer = setTimeout(() => setIsEnteringOrExitingOverlay(false), sidebarTransitionMs);
		return () => clearTimeout(timer);
	}, [isOverlaying, sidebarTransitionMs]);

	return (
		<Box
			position="sticky"
			top={top}
			height={height}
			flexGrow={0}
			flexShrink={0}
			flexBasis={flexBasis}
			// Without this, flex's `min-width: auto` default floors the closed
			// (flex-basis: 0) panel at its content's width, not 0.
			minW="0"
			overflow="hidden"
			zIndex="docked"
			transition={isResizing ? 'none' : `flex-basis ${PANEL_OPEN_CLOSE_TRANSITION_MS}ms ${sidebarTransitionEasing}`}
		>
			<Box
				position={isOverlaying ? 'fixed' : 'absolute'}
				top={isOverlaying ? top : 0}
				// Right-anchored when docked: as flexBasis grows from 0, the left edge
				// is what's revealed — the panel slides out from behind the sidebar.
				left={isOverlaying ? rem(sidebarPx) : undefined}
				right={isOverlaying ? undefined : 0}
				width={isOverlaying ? `calc(100vw - ${rem(sidebarPx)})` : dockedWidth}
				height={isOverlaying ? height : '100%'}
				overflow="hidden"
				borderRight={isOpen && !isFullscreen ? '1px solid' : 'none'}
				borderColor="border.soft"
				zIndex={isOverlaying ? 'overlay' : 'docked'}
				transition={
					isOverlaying && !isEnteringOrExitingOverlay
						? `left ${sidebarTransitionMs}ms ${sidebarTransitionEasing}, width ${sidebarTransitionMs}ms ${sidebarTransitionEasing}`
						: 'none'
				}
			>
				<Box ref={bizyPanelFallbackRef} position="absolute" top={rem(80)} left="calc(50% - 40px)" boxSize={rem(80)} pointerEvents="none" />
				<BizySidePanel gradientTheme="cool-subtle" panelLeftEdge={sidebarPx} />
			</Box>
		</Box>
	);
});
